import React from "react";
import styled from "styled-components";
import SectionShell from "../../homes/SectionShell";
import {
  brand_orange,
  brand_orange_deep,
  brand_blue,
  brand_success,
  narrowScreenMediaQuery,
  tabletScreenMediaQuery,
} from "../../constants/styles";

// The long-form body of the webinar detail page. Three blocks:
//   1. Kategori chips + deskripsi (with optional bullet-list of benefits)
//   2. Cara Mengikuti — streaming platform, join-timing hint, virtual background download
//   3. Sertifikat — adapts copy based on signed-in cert_status or anonymous baseline
function WebinarDetails({ webinar }) {
  const hasCategories =
    Array.isArray(webinar.categories) && webinar.categories.length > 0;
  const hasBenefits =
    Array.isArray(webinar.benefits) && webinar.benefits.length > 0;
  const description = (webinar.description || "").trim();

  return (
    <SectionShell $bg="#ffffff" id="detail-webinar">
      <Grid>
        <Primary>
          <Block>
            <Heading>Tentang Webinar Ini</Heading>
            {hasCategories && (
              <Chips aria-label="Kategori webinar">
                {webinar.categories.map((cat) => (
                  <Chip key={cat}>{cat}</Chip>
                ))}
              </Chips>
            )}
            {description ? (
              <Description>{description}</Description>
            ) : (
              <Muted>Deskripsi webinar belum ditambahkan.</Muted>
            )}
            {hasBenefits && (
              <BenefitList aria-label="Manfaat mengikuti">
                {webinar.benefits.map((b, i) => (
                  <BenefitItem key={`${b}-${i}`}>
                    <Check aria-hidden="true">✓</Check>
                    <span>{b}</span>
                  </BenefitItem>
                ))}
              </BenefitList>
            )}
          </Block>
        </Primary>

        <Secondary>
          <InfoCard>
            <InfoTitle>Cara Mengikuti</InfoTitle>
            <InfoList>
              <InfoRow>
                <InfoIcon aria-hidden="true">🎥</InfoIcon>
                <div>
                  <InfoLabel>Platform Streaming</InfoLabel>
                  <InfoValue>{webinar.streaming_platform || "—"}</InfoValue>
                </div>
              </InfoRow>
              <InfoRow>
                <InfoIcon aria-hidden="true">🕐</InfoIcon>
                <div>
                  <InfoLabel>Kapan Bergabung</InfoLabel>
                  <InfoValue>
                    {webinar.is_done
                      ? "Webinar telah selesai."
                      : webinar.is_live
                        ? "Sedang berlangsung — gabung sekarang."
                        : "Tombol bergabung aktif saat webinar dimulai."}
                  </InfoValue>
                </div>
              </InfoRow>
              {webinar.virtual_background_url && (
                <InfoRow>
                  <InfoIcon aria-hidden="true">🖼️</InfoIcon>
                  <div>
                    <InfoLabel>Virtual Background</InfoLabel>
                    <a
                      href={webinar.virtual_background_url}
                      target="_blank"
                      rel="noreferrer"
                      download
                    >
                      Unduh background webinar
                    </a>
                  </div>
                </InfoRow>
              )}
            </InfoList>
          </InfoCard>

          {webinar.certificate_offered && (
            <InfoCard>
              <InfoTitle>Sertifikat Kemampuan</InfoTitle>
              <CertBody>
                <CertStatus $status={certStatusVariant(webinar)}>
                  {certStatusLabel(webinar)}
                </CertStatus>
                <CertCopy>{certCopy(webinar)}</CertCopy>
                {webinar.certificate_price_idr > 0 && (
                  <CertPrice>
                    Biaya sertifikat:{" "}
                    <strong>
                      Rp {formatIdr(webinar.certificate_price_idr)}
                    </strong>
                  </CertPrice>
                )}
                {certPortalLinkLabel(webinar) && (
                  <CertLink
                    href={
                      webinar.certificate_portal_url || "/sertifikat#webinar"
                    }
                  >
                    {certPortalLinkLabel(webinar)} →
                  </CertLink>
                )}
              </CertBody>
            </InfoCard>
          )}

          {webinar.company_badge && webinar.company_badge.name && (
            <InfoCard>
              <InfoTitle>Webinar Khusus</InfoTitle>
              <CertCopy>
                Tersedia untuk <strong>{webinar.company_badge.name}</strong>.
              </CertCopy>
            </InfoCard>
          )}
        </Secondary>
      </Grid>
    </SectionShell>
  );
}

export default React.memo(WebinarDetails);

// ---- helpers ----------------------------------------------------------------

function formatIdr(n) {
  return Number(n || 0).toLocaleString("id-ID");
}

function certStatusVariant(w) {
  if (w.cert_status === "claimed") return "success";
  if (w.cert_status === "eligible_unclaimed") return "primary";
  if (w.cert_status === "claimable_unpaid") return "warn";
  if (w.cert_status === "expired") return "muted";
  return "neutral";
}

function certStatusLabel(w) {
  switch (w.cert_status) {
    case "claimed":
      return "Telah Diklaim";
    case "eligible_unclaimed":
      return "Siap Diklaim";
    case "claimable_unpaid":
      return "Perlu Pembayaran";
    case "expired":
      return "Masa Klaim Berakhir";
    case "ineligible":
      return "Belum Tersedia";
    default:
      return w.certificate_offered ? "Tersedia untuk Peserta Terdaftar" : "—";
  }
}

function certCopy(w) {
  if (w.cert_status === "claimed") {
    return "Sertifikat sudah diterbitkan. Unduh kapan saja di halaman Sertifikat.";
  }
  if (w.cert_status === "eligible_unclaimed") {
    return "Anda berhak atas sertifikat webinar ini. Klaim di halaman Sertifikat.";
  }
  if (w.cert_status === "claimable_unpaid") {
    return "Sertifikat tersedia namun belum dibayar. Selesaikan pembayaran di halaman Sertifikat untuk menerbitkannya.";
  }
  if (w.cert_status === "expired") {
    return `Masa klaim sertifikat (${w.certificate_claim_duration_days} hari) telah berakhir.`;
  }
  return `Daftar dan hadiri webinar ini untuk berhak atas sertifikat kemampuan${
    w.certificate_claim_duration_days
      ? ` (klaim dalam ${w.certificate_claim_duration_days} hari setelah webinar).`
      : "."
  }`;
}

function certPortalLinkLabel(w) {
  if (w.cert_status === "claimed") return "Lihat & Unduh Sertifikat";
  if (w.cert_status === "claimable_unpaid") return "Bayar Sertifikat";
  if (w.cert_status === "eligible_unclaimed") return "Klaim Sertifikat";
  return null;
}

// ---- styles -----------------------------------------------------------------

const Grid = styled.div`
  display: grid;
  gap: 32px;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);

  @media ${tabletScreenMediaQuery} {
    grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  }
  @media ${narrowScreenMediaQuery} {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const Primary = styled.div`
  min-width: 0;
`;

const Secondary = styled.aside`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Block = styled.div`
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  padding: 24px;

  [data-theme="dark"] & {
    background: #17171e;
    border-color: rgba(255, 255, 255, 0.08);
  }
`;

const Heading = styled.h2`
  font-size: 22px;
  font-weight: 800;
  color: #1f2937;
  margin: 0 0 16px;

  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const Chip = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${brand_orange_deep};
  background: #fef3e5;
  border: 1px solid ${brand_orange};
  padding: 4px 12px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.03em;

  [data-theme="dark"] & {
    background: rgba(239, 135, 38, 0.08);
    color: #fbbf72;
  }
`;

const Description = styled.p`
  color: #374151;
  line-height: 1.7;
  white-space: pre-wrap;
  word-wrap: break-word;
  margin: 0 0 16px;

  [data-theme="dark"] & {
    color: #d1d5db;
  }
`;

const Muted = styled.p`
  color: #9ca3af;
  font-style: italic;
  margin: 0 0 16px;

  [data-theme="dark"] & {
    color: #6b7280;
  }
`;

const BenefitList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #374151;
  line-height: 1.5;

  [data-theme="dark"] & {
    color: #d1d5db;
  }
`;

const Check = styled.span`
  color: ${brand_success};
  font-weight: 800;
  flex-shrink: 0;
`;

const InfoCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;

  [data-theme="dark"] & {
    background: #17171e;
    border-color: rgba(255, 255, 255, 0.08);
  }
`;

const InfoTitle = styled.h3`
  font-size: 16px;
  font-weight: 800;
  color: #1f2937;
  margin: 0 0 12px;

  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;

  a {
    color: ${brand_blue};
    text-decoration: none;
    font-weight: 600;
  }
  a:hover {
    text-decoration: underline;
  }

  [data-theme="dark"] & a {
    color: #93c5fd;
  }
`;

const InfoIcon = styled.span`
  font-size: 20px;
  line-height: 1;
  padding-top: 2px;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
  margin-bottom: 2px;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const InfoValue = styled.div`
  font-size: 14px;
  color: #1f2937;
  font-weight: 600;

  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const CertBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CertStatus = styled.div`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ $status }) =>
    $status === "success"
      ? brand_success
      : $status === "primary"
        ? brand_orange_deep
        : $status === "warn"
          ? "#b45309"
          : $status === "muted"
            ? "#9ca3af"
            : "#6b7280"};
`;

const CertCopy = styled.p`
  color: #374151;
  line-height: 1.5;
  margin: 0;
  font-size: 14px;

  [data-theme="dark"] & {
    color: #d1d5db;
  }
`;

const CertPrice = styled.div`
  font-size: 13px;
  color: #6b7280;

  strong {
    color: #1f2937;
  }
  [data-theme="dark"] & {
    color: #9ca3af;
  }
  [data-theme="dark"] & strong {
    color: #f3f4f6;
  }
`;

const CertLink = styled.a`
  align-self: flex-start;
  font-size: 13px;
  font-weight: 700;
  color: ${brand_orange_deep};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
